import { useEffect, useState, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Switch,
  FormControlLabel,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import { jsPDF } from 'jspdf';
import Loader from './Loader';
import api from '../../../services/api';
import './ConfiguracoesTab.css';
import {
  defaultHorarios,
  parseOrNull,
  normalizeRestauranteComparable,
  normalizePrincipalAddressComparable,
  isPrincipalAddressComparableEqual,
  normalizeHorarioComparable,
  buildHorarioComparableMap,
  isHorarioComparableEqual,
  buildHorarioPayload,
  buildHorariosState,
  emptyLocalidade,
  extractApiErrorMessage,
  parsePercent,
  loadImage,
  validateHorarios,
  isDiaFechado,
  buildHorariosResumo,
  buildExtrasPorDia,
  buildClubeScheduleDays,
  buildHorariosPorDia,
} from './configuracoesTab.utils';
import ConfigAreaPanel from './components/ConfigAreaPanel';
import ConfigHorariosPanel from './components/ConfigHorariosPanel';
import ConfigQrPanel from './components/ConfigQrPanel';
import ConfigQrPrintPanel from './components/ConfigQrPrintPanel';
import ConfiguracoesTabModals from './components/ConfiguracoesTabModals';
import {
  QR_CHECKIN_API_SIZE,
  QR_DISPLAY_TEMPLATE,
} from './constants/qrDisplay';

const ConfiguracoesTab = ({
  onHorarioChange,
  forceOpenConfigModal = false,
  onConfigModalClose,
  onConfigSaved,
}) => {
  const [form, setForm] = useState({ lat: '', long: '', raio: '', intervalo_checkin: '' });
  const [horariosBase, setHorariosBase] = useState(defaultHorarios);
  const [horariosExtras, setHorariosExtras] = useState([]);
  const [horariosRemovidos, setHorariosRemovidos] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);
  const [loading, setLoading] = useState(true);
  const [localidades, setLocalidades] = useState([]);
  const [checkinSlug, setCheckinSlug] = useState('');
  const [checkinUrl, setCheckinUrl] = useState('');
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [qrLoading, setQrLoading] = useState(false);
  const [localidadeDialogOpen, setLocalidadeDialogOpen] = useState(false);
  const [localidadeForm, setLocalidadeForm] = useState({ ...emptyLocalidade });
  const [savingLocalidade, setSavingLocalidade] = useState(false);
  const [enderecoQuery, setEnderecoQuery] = useState('');
  const [enderecoResultados, setEnderecoResultados] = useState([]);
  const [buscandoEndereco, setBuscandoEndereco] = useState(false);
  const [enderecoPrincipalQuery, setEnderecoPrincipalQuery] = useState('');
  const [enderecoPrincipalResultados, setEnderecoPrincipalResultados] = useState([]);
  const [buscandoEnderecoPrincipal, setBuscandoEnderecoPrincipal] = useState(false);
  const [principalAddressDetails, setPrincipalAddressDetails] = useState(
    normalizePrincipalAddressComparable(),
  );
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [clubeModuleRule, setClubeModuleRule] = useState(null);
  const [clubeModuleRuleLoading, setClubeModuleRuleLoading] = useState(true);
  const [clubeModuleRuleSaving, setClubeModuleRuleSaving] = useState(false);
  const initialRestauranteRef = useRef(normalizeRestauranteComparable());
  const initialPrincipalAddressRef = useRef(normalizePrincipalAddressComparable());
  const initialHorariosRef = useRef(new Map());
  const diaAtual = new Date().getDay();
  const clubeModuleAtivo = clubeModuleRule?.ativo !== false;
  const clubeSharedFieldsLocked = clubeModuleRuleLoading;

  const printRef = useRef(null);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const applyClubeConfigPayload = (payload = {}) => {
    const restauranteData = payload?.restaurante || {};
    setForm({
      lat: restauranteData.lat ?? '',
      long: restauranteData.long ?? '',
      raio: restauranteData.raio ?? '',
      intervalo_checkin: restauranteData.intervalo_checkin ?? '',
    });
    initialRestauranteRef.current = normalizeRestauranteComparable(restauranteData);

    const principalAddress = normalizePrincipalAddressComparable(restauranteData);
    setPrincipalAddressDetails(principalAddress);
    initialPrincipalAddressRef.current = principalAddress;
    if (principalAddress.formatted_address) {
      setEnderecoPrincipalQuery(principalAddress.formatted_address);
    } else {
      setEnderecoPrincipalQuery('');
    }

    const horariosData = Array.isArray(payload?.horarios) ? payload.horarios : [];
    const { base, extras } = buildHorariosState(horariosData);
    setHorariosBase(base);
    setHorariosExtras(extras);
    setHorariosRemovidos([]);
    initialHorariosRef.current = buildHorarioComparableMap(horariosData);

    setCheckinSlug(restauranteData?.checkin_slug ? String(restauranteData.checkin_slug).trim() : '');
    setLocalidades(Array.isArray(payload?.localidades) ? payload.localidades : []);

    const clubeRule = payload?.module_rule || null;
    setClubeModuleRule(
      clubeRule
        ? { ...clubeRule, ativo: clubeRule?.ativo !== false }
        : null,
    );
  };

  const ensureClubeOwnConfig = () => {
    if (clubeModuleRuleLoading) {
      showSnackbar('Aguarde a validação da regra do módulo antes de editar.', 'warning');
      return false;
    }
    return true;
  };

  const handleToggleClubeAtivo = async (nextAtivo) => {
    if (clubeModuleRuleSaving) {
      return;
    }

    setClubeModuleRuleSaving(true);
    try {
      const { data } = await api.put(
        '/config/core-config/module-rules/clube_mesa',
        { ativo: nextAtivo }
      );
      setClubeModuleRule((prev) => ({
        ...(prev || { id: 'clube_mesa', usar_regra_global: false }),
        ...(data || {}),
        ativo: data?.ativo !== undefined ? data.ativo : nextAtivo,
      }));
      showSnackbar(
        nextAtivo
          ? 'Clube de Mesa ativado com sucesso.'
          : 'Clube de Mesa desativado. Os check-ins foram bloqueados.',
        'success'
      );
    } catch (err) {
      console.error('Erro ao atualizar status do Clube de Mesa:', err);
      showSnackbar(
        extractApiErrorMessage(err, 'Não foi possível atualizar o status do Clube de Mesa.'),
        'error',
      );
    } finally {
      setClubeModuleRuleSaving(false);
    }
  };

  const renderDisplayCanvas = async () => {
    if (!qrDataUrl) throw new Error('QR indisponível');
    const [background, qrImage] = await Promise.all([
      loadImage(QR_DISPLAY_TEMPLATE.backgroundUrl),
      loadImage(qrDataUrl),
    ]);

    const canvas = document.createElement('canvas');
    const width = background.naturalWidth || background.width;
    const height = background.naturalHeight || background.height;
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext('2d');
    ctx.drawImage(background, 0, 0, width, height);

    const angle = (parseFloat(QR_DISPLAY_TEMPLATE.rotation) * Math.PI) / 180;
    const targetW = parsePercent(QR_DISPLAY_TEMPLATE.size.width) * width;
    const targetH = parsePercent(QR_DISPLAY_TEMPLATE.size.height) * height;
    const scale = Math.min(targetW / qrImage.width, targetH / qrImage.height);
    const drawW = qrImage.width * scale;
    const drawH = qrImage.height * scale;

    QR_DISPLAY_TEMPLATE.placements.forEach((placement) => {
      const centerX = parsePercent(placement.left) * width;
      const centerY = parsePercent(placement.top) * height;
      ctx.save();
      ctx.translate(centerX, centerY);
      ctx.rotate(angle);
      ctx.drawImage(qrImage, -drawW / 2, -drawH / 2, drawW, drawH);
      ctx.restore();
    });

    return canvas;
  };

  useEffect(() => {
    const fetchConfigs = async () => {
      try {
        const { data } = await api.get('/config/clube-config');
        applyClubeConfigPayload(data || {});
      } catch (err) {
        console.error('Erro ao carregar configurações:', err);
      } finally {
        setClubeModuleRuleLoading(false);
        setLoading(false);
      }
    };

    fetchConfigs();
  }, []);

  useEffect(() => {
    const slug = checkinSlug.trim();
    let active = true;

    if (!slug) {
      setCheckinUrl('');
      setQrDataUrl('');
      return undefined;
    }

    setQrLoading(true);
    api.get('/qrcode/checkin', { params: { slug, size: QR_CHECKIN_API_SIZE } })
      .then(({ data }) => {
        if (!active) return;
        const payload = data?.data || data || {};
        setCheckinUrl(payload.url || '');
        setQrDataUrl(payload.data_url || '');
      })
      .catch((err) => {
        if (!active) return;
        console.error('Erro ao gerar QR Code:', err);
        showSnackbar('Não foi possível gerar o QR Code.', 'error');
        setCheckinUrl('');
        setQrDataUrl('');
      })
      .finally(() => {
        if (!active) return;
        setQrLoading(false);
      });

    return () => {
      active = false;
    };
  }, [checkinSlug]);

  useEffect(() => {
    if (forceOpenConfigModal) {
      setModalOpen(true);
    }
  }, [forceOpenConfigModal]);

  if (loading) return <Loader />;

  const handleCopyCheckinUrl = async () => {
    if (!checkinUrl) {
      showSnackbar('Link de check-in indisponível.', 'warning');
      return;
    }

    try {
      await navigator.clipboard.writeText(checkinUrl);
      showSnackbar('Link copiado.', 'success');
    } catch (err) {
      console.error('Erro ao copiar link:', err);
      showSnackbar('Não foi possível copiar o link.', 'error');
    }
  };

  const handleDownloadPng = async () => {
    try {
      const canvas = await renderDisplayCanvas();
      const dataUrl = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.href = dataUrl;
      link.download = `checkin-${checkinSlug || 'qr'}.png`;
      link.click();
    } catch (err) {
      console.error('Erro ao gerar PNG:', err);
      showSnackbar('Não foi possível gerar o PNG.', 'error');
    }
  };

  const handleDownloadPdf = async () => {
    try {
      const canvas = await renderDisplayCanvas();
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pageWidth - 20;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      const x = (pageWidth - imgWidth) / 2;
      const y = (pageHeight - imgHeight) / 2;
      pdf.addImage(imgData, 'PNG', x, y, imgWidth, imgHeight);
      pdf.save(`checkin-${checkinSlug || 'qr'}.pdf`);
    } catch (err) {
      console.error('Erro ao gerar PDF:', err);
      showSnackbar('Não foi possível gerar o PDF.', 'error');
    }
  };

  const handlePrintCard = async () => {
    try {
      const canvas = await renderDisplayCanvas();
      const dataUrl = canvas.toDataURL('image/png');
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        showSnackbar('Não foi possível abrir a impressão.', 'error');
        return;
      }
      printWindow.document.write(`
        <html>
          <head><title>Impressão QR Code</title></head>
          <body style="margin:0; display:flex; align-items:center; justify-content:center;">
            <img src="${dataUrl}" style="max-width:100%; height:auto;" />
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.focus();
      printWindow.print();
      printWindow.close();
    } catch (err) {
      console.error('Erro ao imprimir:', err);
      showSnackbar('Não foi possível imprimir.', 'error');
    }
  };

  const handleOpenModal = () => {
    setModalOpen(true);
  };
  const handleCloseModal = () => {
    if (savingConfig) return;
    setModalOpen(false);
    setEnderecoPrincipalQuery('');
    setEnderecoPrincipalResultados([]);
    setBuscandoEnderecoPrincipal(false);
    if (onConfigModalClose) onConfigModalClose();
  };

  const handleOpenLocalidadeDialog = (localidade = null) => {
    if (!ensureClubeOwnConfig()) return;
    if (localidade) {
      setLocalidadeForm({
        id: localidade.id,
        nome: localidade.nome || '',
        lat: localidade.lat !== null && localidade.lat !== undefined ? String(localidade.lat) : '',
        long: localidade.long !== null && localidade.long !== undefined ? String(localidade.long) : '',
        raio: localidade.raio !== null && localidade.raio !== undefined ? String(localidade.raio) : '',
        logradouro: localidade.logradouro || '',
        numero: localidade.numero || '',
        complemento: localidade.complemento || '',
        bairro: localidade.bairro || '',
        cidade: localidade.cidade || '',
        estado: localidade.estado || '',
        cep: localidade.cep || '',
        place_id: localidade.place_id || '',
        formatted_address: localidade.formatted_address || '',
      });
    } else {
      setLocalidadeForm({ ...emptyLocalidade });
    }
    setEnderecoQuery('');
    setEnderecoResultados([]);
    setBuscandoEndereco(false);
    setLocalidadeDialogOpen(true);
  };

  const handleCloseLocalidadeDialog = () => {
    setLocalidadeDialogOpen(false);
    setLocalidadeForm({ ...emptyLocalidade });
    setEnderecoQuery('');
    setEnderecoResultados([]);
    setBuscandoEndereco(false);
  };

  const handleLocalidadeFieldChange = (field, value) => {
    setLocalidadeForm((prev) => ({ ...prev, [field]: value }));
  };

  const handlePrincipalAddressFieldChange = (field, value) => {
    setPrincipalAddressDetails((prev) => ({ ...prev, [field]: value }));
  };

  const handleBuscarEndereco = async () => {
    const endereco = enderecoQuery.trim();
    if (!endereco) {
      showSnackbar('Informe um endereço para buscar.', 'warning');
      return;
    }

    setBuscandoEndereco(true);
    setEnderecoResultados([]);
    try {
      const { data } = await api.get(`/config/geocode`, {
        params: { address: endereco },
      });
      const resultados = Array.isArray(data) ? data : [];
      setEnderecoResultados(resultados);
      if (resultados.length === 0) {
        showSnackbar('Nenhum endereço encontrado.', 'warning');
      }
    } catch (err) {
      console.error('Erro ao buscar endereço:', err);
      showSnackbar('Não foi possível buscar o endereço. Tente novamente.', 'error');
    } finally {
      setBuscandoEndereco(false);
    }
  };

  const handleSelecionarEndereco = (resultado) => {
    if (!resultado) return;
    setLocalidadeForm((prev) => ({
      ...prev,
      lat: resultado.lat != null ? String(resultado.lat) : prev.lat,
      long: resultado.long != null ? String(resultado.long) : prev.long,
      nome: prev.nome?.trim() ? prev.nome : (resultado.formatted_address || prev.nome),
      logradouro: resultado.street || prev.logradouro,
      numero: resultado.number || prev.numero,
      bairro: resultado.neighborhood || prev.bairro,
      cidade: resultado.city || prev.cidade,
      estado: resultado.state || prev.estado,
      cep: resultado.postal_code || prev.cep,
      place_id: resultado.place_id || prev.place_id,
      formatted_address: resultado.formatted_address || prev.formatted_address,
    }));
    setEnderecoQuery(resultado.formatted_address || enderecoQuery);
    setEnderecoResultados([]);
  };

  const handleBuscarEnderecoPrincipal = async () => {
    const endereco = enderecoPrincipalQuery.trim();
    if (!endereco) {
      showSnackbar('Informe um endereço para buscar.', 'warning');
      return;
    }

    setBuscandoEnderecoPrincipal(true);
    setEnderecoPrincipalResultados([]);
    try {
      const { data } = await api.get(`/config/geocode`, {
        params: { address: endereco },
      });
      const resultados = Array.isArray(data) ? data : [];
      setEnderecoPrincipalResultados(resultados);
      if (resultados.length === 0) {
        showSnackbar('Nenhum endereço encontrado.', 'warning');
      }
    } catch (err) {
      console.error('Erro ao buscar endereço principal:', err);
      showSnackbar('Não foi possível buscar o endereço. Tente novamente.', 'error');
    } finally {
      setBuscandoEnderecoPrincipal(false);
    }
  };

  const handleSelecionarEnderecoPrincipal = (resultado) => {
    if (!resultado) return;
    setForm((prev) => ({
      ...prev,
      lat: resultado.lat != null ? String(resultado.lat) : prev.lat,
      long: resultado.long != null ? String(resultado.long) : prev.long,
    }));
    setPrincipalAddressDetails(normalizePrincipalAddressComparable({
      logradouro: resultado.street,
      numero: resultado.number,
      bairro: resultado.neighborhood,
      cidade: resultado.city,
      estado: resultado.state,
      cep: resultado.postal_code,
      place_id: resultado.place_id,
      formatted_address: resultado.formatted_address,
    }));
    setEnderecoPrincipalQuery(resultado.formatted_address || enderecoPrincipalQuery);
    setEnderecoPrincipalResultados([]);
  };

  const handleDeleteLocalidade = async (id) => {
    if (!ensureClubeOwnConfig()) return;

    const confirmacao = window.confirm('Tem certeza que deseja excluir esta localidade?');
    if (!confirmacao) return;

    try {
      await api.delete(`/fidelidade/localidades/${id}`);
      setLocalidades((prev) => prev.filter((item) => item.id !== id));
      showSnackbar('Localidade excluída.', 'success');
    } catch (err) {
      console.error('Erro ao excluir localidade:', err);
      showSnackbar('Não foi possível excluir a localidade. Tente novamente.', 'error');
    }
  };

  const handleSaveLocalidade = async () => {
    if (!ensureClubeOwnConfig()) return;

    if (!localidadeForm.nome?.trim()) {
      showSnackbar('Informe um nome para a localidade.', 'warning');
      return;
    }

    const latValue = parseOrNull(localidadeForm.lat, parseFloat);
    if (Number.isNaN(latValue)) {
      showSnackbar('Latitude inválida. Utilize apenas números.', 'error');
      return;
    }

    const longValue = parseOrNull(localidadeForm.long, parseFloat);
    if (Number.isNaN(longValue)) {
      showSnackbar('Longitude inválida. Utilize apenas números.', 'error');
      return;
    }

    const hasLat = latValue !== null;
    const hasLong = longValue !== null;
    if (hasLat !== hasLong) {
      showSnackbar('Latitude e longitude da localidade devem ser informadas juntas.', 'warning');
      return;
    }
    if (hasLat && (latValue < -90 || latValue > 90)) {
      showSnackbar('Latitude da localidade inválida. Use um valor entre -90 e 90.', 'warning');
      return;
    }
    if (hasLong && (longValue < -180 || longValue > 180)) {
      showSnackbar('Longitude da localidade inválida. Use um valor entre -180 e 180.', 'warning');
      return;
    }

    const raioValue = parseOrNull(localidadeForm.raio, (value) => parseInt(value, 10));
    if (Number.isNaN(raioValue)) {
      showSnackbar('Raio inválido. Utilize apenas números.', 'error');
      return;
    }
    if (raioValue !== null && (raioValue <= 0 || raioValue > 10000)) {
      showSnackbar('Raio da localidade inválido. Use um valor entre 1 e 10000 metros.', 'warning');
      return;
    }

    const estadoNormalizado = String(localidadeForm.estado || '').trim().toUpperCase();
    if (estadoNormalizado && !/^[A-Z]{2}$/.test(estadoNormalizado)) {
      showSnackbar('UF da localidade inválida. Use 2 letras.', 'warning');
      return;
    }

    const cepNormalizado = String(localidadeForm.cep || '').replace(/\D/g, '');
    if (String(localidadeForm.cep || '').trim() && cepNormalizado.length !== 8) {
      showSnackbar('CEP da localidade inválido. Use 8 dígitos.', 'warning');
      return;
    }

    const payload = {
      nome: localidadeForm.nome.trim(),
      lat: latValue,
      long: longValue,
      raio: raioValue,
      logradouro: localidadeForm.logradouro?.trim() || null,
      numero: localidadeForm.numero?.trim() || null,
      complemento: localidadeForm.complemento?.trim() || null,
      bairro: localidadeForm.bairro?.trim() || null,
      cidade: localidadeForm.cidade?.trim() || null,
      estado: estadoNormalizado || null,
      cep: cepNormalizado || null,
      place_id: localidadeForm.place_id?.trim() || null,
      formatted_address: localidadeForm.formatted_address?.trim() || null,
    };

    setSavingLocalidade(true);
    try {
      if (localidadeForm.id) {
        const { data } = await api.put(`/fidelidade/localidades/${localidadeForm.id}`, payload);
        setLocalidades((prev) =>
          prev.map((item) => (item.id === data.id ? data : item))
        );
      } else {
        const { data } = await api.post(`/fidelidade/localidades`, payload);
        setLocalidades((prev) => [...prev, data]);
      }
      showSnackbar(
        localidadeForm.id ? 'Localidade atualizada com sucesso.' : 'Localidade criada com sucesso.',
        'success'
      );
      handleCloseLocalidadeDialog();
    } catch (err) {
      console.error('Erro ao salvar localidade:', err);
      showSnackbar('Não foi possível salvar a localidade. Tente novamente.', 'error');
    } finally {
      setSavingLocalidade(false);
    }
  };

  const handleChange = (field, value) => setForm({ ...form, [field]: value });

  const handleBaseHorarioChange = (index, campo, valor) => {
    setHorariosBase((prev) => {
      const atualizados = [...prev];
      atualizados[index] = { ...atualizados[index], [campo]: valor };
      return atualizados;
    });
  };

  const handleExtraHorarioChange = (index, campo, valor) => {
    const novoValor = campo === 'dia_semana' ? Number(valor) : valor;
    setHorariosExtras((prev) => {
      const atualizados = [...prev];
      atualizados[index] = { ...atualizados[index], [campo]: novoValor };
      return atualizados;
    });
  };

  const handleAddHorarioExtra = (dia) => {
    const diaSelecionado = Number.isInteger(dia) ? dia : diaAtual;
    setHorariosExtras((prev) => ([
      ...prev,
      { id: null, dia_semana: diaSelecionado, abre: '', fecha: '', fechado: false },
    ]));
  };

  const handleRemoveHorarioExtra = (index) => {
    setHorariosExtras((prev) => {
      const alvo = prev[index];
      if (alvo?.id) {
        setHorariosRemovidos((removidos) => [...removidos, alvo.id]);
      }
      return prev.filter((_, i) => i !== index);
    });
  };

  const getFechadoPorDia = (dia) => isDiaFechado(horariosBase, dia);

  const handleSubmit = async () => {
    if (savingConfig) return;

    const extrasComFechado = horariosExtras.map((horario) => ({
      ...horario,
      fechado: getFechadoPorDia(horario.dia_semana),
    }));

    const horariosParaSalvar = [...horariosBase, ...extrasComFechado];

    if (!clubeSharedFieldsLocked) {
      const horarioValidation = validateHorarios(horariosParaSalvar);
      if (!horarioValidation.ok) {
        showSnackbar(horarioValidation.message, 'warning');
        return;
      }
    }

    const latValue = parseOrNull(form.lat, parseFloat);
    if (Number.isNaN(latValue)) {
      showSnackbar('Latitude inválida. Utilize apenas números.', 'error');
      return;
    }

    const longValue = parseOrNull(form.long, parseFloat);
    if (Number.isNaN(longValue)) {
      showSnackbar('Longitude inválida. Utilize apenas números.', 'error');
      return;
    }

    const hasLat = latValue !== null;
    const hasLong = longValue !== null;
    if (hasLat !== hasLong) {
      showSnackbar('Latitude e longitude devem ser informadas juntas.', 'warning');
      return;
    }
    if (hasLat && (latValue < -90 || latValue > 90)) {
      showSnackbar('Latitude inválida. Use um valor entre -90 e 90.', 'warning');
      return;
    }
    if (hasLong && (longValue < -180 || longValue > 180)) {
      showSnackbar('Longitude inválida. Use um valor entre -180 e 180.', 'warning');
      return;
    }

    const raioValue = parseOrNull(form.raio, (value) => parseInt(value, 10));
    if (Number.isNaN(raioValue)) {
      showSnackbar('Raio inválido. Utilize apenas números.', 'error');
      return;
    }
    if (raioValue !== null && (raioValue <= 0 || raioValue > 10000)) {
      showSnackbar('Raio inválido. Use um valor entre 1 e 10000 metros.', 'warning');
      return;
    }

    const intervaloCheckin = parseOrNull(form.intervalo_checkin, (value) => parseInt(value, 10));
    if (Number.isNaN(intervaloCheckin)) {
      showSnackbar('Intervalo entre check-ins inválido. Use apenas números inteiros.', 'error');
      return;
    }
    if (intervaloCheckin !== null && (intervaloCheckin < 1 || intervaloCheckin > 168)) {
      showSnackbar('Intervalo entre check-ins inválido. Use um valor entre 1 e 168 horas.', 'warning');
      return;
    }

    const restauranteComparavelAtual = normalizeRestauranteComparable({
      lat: latValue,
      long: longValue,
      raio: raioValue,
      intervalo_checkin: intervaloCheckin,
    });

    const restaurantePayload = {};
    Object.entries(restauranteComparavelAtual).forEach(([field, value]) => {
      if (initialRestauranteRef.current?.[field] !== value) {
        restaurantePayload[field] = value;
      }
    });

    const principalAddressComparable = normalizePrincipalAddressComparable(principalAddressDetails);
    const principalAddressChanged = !isPrincipalAddressComparableEqual(
      initialPrincipalAddressRef.current,
      principalAddressComparable,
    );
    if (!clubeSharedFieldsLocked && principalAddressChanged) {
      restaurantePayload.principal_address_details = principalAddressComparable;
    }

    const removidosUnicos = !clubeSharedFieldsLocked
      ? Array.from(new Set(horariosRemovidos.map((id) => String(id))))
      : [];

    let hasHorarioMutation = removidosUnicos.length > 0;

    if (!clubeSharedFieldsLocked) {
      horariosParaSalvar.forEach((horario) => {
        const payload = buildHorarioPayload(horario);

        if (!horario.id) {
          hasHorarioMutation = true;
          return;
        }

        const id = String(horario.id);
        const baseline = initialHorariosRef.current.get(id);
        const atual = normalizeHorarioComparable(payload);
        if (!baseline || !isHorarioComparableEqual(baseline, atual)) {
          hasHorarioMutation = true;
        }
      });
    }

    const hasRestauranteMutation = Object.keys(restaurantePayload).length > 0;

    if (!hasRestauranteMutation && !hasHorarioMutation) {
      showSnackbar('Nenhuma alteração para salvar.', 'info');
      return;
    }

    const savePayload = { restaurante: restaurantePayload };
    if (!clubeSharedFieldsLocked) {
      savePayload.horarios = horariosParaSalvar.map((horario) => buildHorarioPayload(horario));
    }

    setSavingConfig(true);
    try {
      const { data } = await api.put('/config/clube-config', savePayload);

      applyClubeConfigPayload(data || {});
      setHorariosRemovidos([]);

      showSnackbar('Configurações salvas com sucesso.', 'success');

      const savedUsesGlobal = Boolean(data?.module_rule?.usar_regra_global);
      if (!savedUsesGlobal && hasHorarioMutation && onHorarioChange) onHorarioChange();
      if (onConfigSaved) onConfigSaved(data?.restaurante || { ...form, ...restaurantePayload });
      handleCloseModal();
    } catch (err) {
      console.error('Erro ao salvar configurações:', err);
      showSnackbar('Erro ao salvar configurações. Tente novamente.', 'error');
    } finally {
      setSavingConfig(false);
    }
  };
  const horariosResumo = buildHorariosResumo(horariosBase, horariosExtras);
  const extrasPorDia = buildExtrasPorDia(horariosExtras);
  const clubeScheduleDays = buildClubeScheduleDays(horariosBase, extrasPorDia);
  const updateClubeSchedulePeriod = (dayIndex, periodIndex, field, value) => {
    const targetField = field === 'start' ? 'abre' : 'fecha';
    if (periodIndex === 0) {
      handleBaseHorarioChange(dayIndex, targetField, value);
      return;
    }
    const extra = (extrasPorDia[dayIndex] || [])[periodIndex - 1];
    if (extra) handleExtraHorarioChange(extra._index, targetField, value);
  };
  const removeClubeSchedulePeriod = (dayIndex, periodIndex) => {
    const extras = extrasPorDia[dayIndex] || [];
    if (periodIndex > 0) {
      const extra = extras[periodIndex - 1];
      if (extra) handleRemoveHorarioExtra(extra._index);
      return;
    }
    const [nextBase] = extras;
    if (!nextBase) {
      handleBaseHorarioChange(dayIndex, 'fechado', true);
      return;
    }
    setHorariosBase((prev) => prev.map((item, index) => index === dayIndex
      ? { ...item, abre: nextBase.abre, fecha: nextBase.fecha }
      : item));
    handleRemoveHorarioExtra(nextBase._index);
  };
  const replicateClubeScheduleToAll = (period) => {
    setHorariosBase((prev) => prev.map((item) => ({
      ...item,
      fechado: false,
      abre: period.start,
      fecha: period.end,
    })));
    setHorariosExtras((prev) => {
      const removedIds = prev.map((item) => item.id).filter(Boolean);
      if (removedIds.length) {
        setHorariosRemovidos((current) => [...current, ...removedIds]);
      }
      return [];
    });
  };
  const hasQrCode = Boolean(qrDataUrl);
  const horariosPorDia = buildHorariosPorDia(horariosResumo, horariosBase);

  return (
    <Box sx={{ borderRadius: 2, p: 3 }}>
      <Box display="flex" alignItems="center" gap={1} mb={1}>
        <SettingsIcon color="primary" />
        <Typography variant="h6" fontWeight="bold">
          Configurações do Clube de Mesa
        </Typography>
	      </Box>

	      <Paper
	        variant="outlined"
	        sx={{
	          p: 2,
	          mb: 2,
	          borderRadius: 2,
	          borderColor: !clubeModuleAtivo
	            ? '#ef4444'
	            : '#22d3ee',
	          backgroundColor: !clubeModuleAtivo
	            ? '#fef2f2'
	            : '#ecfeff',
	        }}
	      >
	        <Box
	          display="flex"
	          flexDirection={{ xs: 'column', md: 'row' }}
	          gap={2}
	          alignItems={{ md: 'center' }}
	          justifyContent="space-between"
	        >
	          <Box>
	            <Typography variant="subtitle2" fontWeight="bold">
	              {!clubeModuleAtivo
		                ? 'O Clube de Mesa está desativado. Os check-ins ficam bloqueados até a reativação.'
	                : 'O Clube de Mesa usa configuração própria para área e horários.'}
	            </Typography>
	            <Typography variant="body2" color="text.secondary">
	              {clubeModuleRuleLoading
		                ? 'Validando regra do módulo...'
	                : (!clubeModuleAtivo
	                  ? 'Você pode ajustar as configurações antes de ativar novamente o Clube.'
	                  : 'As alterações desta tela são aplicadas somente ao Clube de Mesa.')}
	            </Typography>
	          </Box>
	          <Box display="flex" gap={1} flexWrap="wrap">
	            <FormControlLabel
	              control={(
	                <Switch
	                  checked={clubeModuleAtivo}
	                  onChange={(event) => handleToggleClubeAtivo(event.target.checked)}
	                  disabled={clubeModuleRuleLoading || clubeModuleRuleSaving}
	                  color={clubeModuleAtivo ? 'success' : 'default'}
	                />
	              )}
	              label={
	                clubeModuleRuleSaving
	                  ? 'Salvando...'
	                  : (clubeModuleAtivo ? 'Clube ativo' : 'Clube desativado')
	              }
	              sx={{ m: 0, '& .MuiFormControlLabel-label': { fontSize: 13, fontWeight: 600 } }}
	            />
	          </Box>
	        </Box>
	      </Paper>

	      <Paper variant="outlined" sx={{ p: 2, mb: 4, borderRadius: 2, backgroundColor: '#fafafa' }}>
        <Typography variant="body2" color="text.secondary">
          Qualquer mudança aqui afeta imediatamente os check-ins.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Recomendamos revisar essas opções apenas em casos específicos.
        </Typography>
      </Paper>

      <ConfigAreaPanel
        form={form}
        localidades={localidades}
        loading={clubeModuleRuleLoading}
        onOpenLocalidadeDialog={handleOpenLocalidadeDialog}
        onDeleteLocalidade={handleDeleteLocalidade}
      />

      <ConfigHorariosPanel
        horariosPorDia={horariosPorDia}
        onOpenModal={handleOpenModal}
      />

      <ConfigQrPanel
        checkinSlug={checkinSlug}
        checkinUrl={checkinUrl}
        qrDataUrl={qrDataUrl}
        qrLoading={qrLoading}
        hasQrCode={hasQrCode}
        onCopyCheckinUrl={handleCopyCheckinUrl}
      />

      {checkinSlug ? (
        <ConfigQrPrintPanel
          printRef={printRef}
          qrDataUrl={qrDataUrl}
          qrLoading={qrLoading}
          hasQrCode={hasQrCode}
          onDownloadPng={handleDownloadPng}
          onDownloadPdf={handleDownloadPdf}
          onPrintCard={handlePrintCard}
        />
      ) : null}

      <ConfiguracoesTabModals
        modalOpen={modalOpen}
        savingConfig={savingConfig}
        handleCloseModal={handleCloseModal}
        handleSubmit={handleSubmit}
        clubeModuleRuleLoading={clubeModuleRuleLoading}
        clubeSharedFieldsLocked={clubeSharedFieldsLocked}
        principalAddressDetails={principalAddressDetails}
        form={form}
        enderecoPrincipalQuery={enderecoPrincipalQuery}
        buscandoEnderecoPrincipal={buscandoEnderecoPrincipal}
        enderecoPrincipalResultados={enderecoPrincipalResultados}
        setEnderecoPrincipalQuery={setEnderecoPrincipalQuery}
        setEnderecoPrincipalResultados={setEnderecoPrincipalResultados}
        handleBuscarEnderecoPrincipal={handleBuscarEnderecoPrincipal}
        handleSelecionarEnderecoPrincipal={handleSelecionarEnderecoPrincipal}
        handleChange={handleChange}
        handlePrincipalAddressFieldChange={handlePrincipalAddressFieldChange}
        clubeScheduleDays={clubeScheduleDays}
        handleBaseHorarioChange={handleBaseHorarioChange}
        handleAddHorarioExtra={handleAddHorarioExtra}
        updateClubeSchedulePeriod={updateClubeSchedulePeriod}
        removeClubeSchedulePeriod={removeClubeSchedulePeriod}
        replicateClubeScheduleToAll={replicateClubeScheduleToAll}
        localidadeDialogOpen={localidadeDialogOpen}
        handleCloseLocalidadeDialog={handleCloseLocalidadeDialog}
        localidadeForm={localidadeForm}
        handleLocalidadeFieldChange={handleLocalidadeFieldChange}
        enderecoQuery={enderecoQuery}
        setEnderecoQuery={setEnderecoQuery}
        handleBuscarEndereco={handleBuscarEndereco}
        buscandoEndereco={buscandoEndereco}
        enderecoResultados={enderecoResultados}
        handleSelecionarEndereco={handleSelecionarEndereco}
        savingLocalidade={savingLocalidade}
        handleSaveLocalidade={handleSaveLocalidade}
        snackbar={snackbar}
        setSnackbar={setSnackbar}
      />
    </Box>
  );
};

export default ConfiguracoesTab;

